import { useState } from "react";

import { api, type Vehicle } from "../api";
import { UNCLASSIFIED, classesOf, inClass, sameClass } from "../vehicleClass";
import { useAction, useLoad } from "../useLoad";
import { TournamentControl } from "./TournamentControl";

interface Props {
  teamId: string;
  /** The signed-in member. Requirement 9.4 makes "mine" a different set from "everyone's". */
  mySub: string;
  revision: number;
  onError: (message: string) => void;
}

/** Requirement 15 fixes the field sizes. There are no byes anywhere, so these are the only ones. */
const SIZES = [8, 16, 32] as const;
const WIDTHS = [
  { value: 2, label: "Two at a time", hint: "Winner advances. More races, simpler calls." },
  { value: 4, label: "Four at a time", hint: "Top two advance. About half as many races." },
] as const;

export function Tournaments({ teamId, mySub, revision, onError }: Props) {
  const [openId, setOpenId] = useState<string | null>(null);
  const list = useLoad(() => api.tournaments(teamId), [teamId, revision], onError);

  if (openId) {
    return (
      <TournamentControl
        teamId={teamId}
        mySub={mySub}
        tournamentId={openId}
        revision={revision}
        onError={onError}
        onClose={() => {
          setOpenId(null);
          list.reload();
        }}
      />
    );
  }

  const tournaments = list.data?.tournaments ?? [];
  const live = tournaments.filter((entry) => entry.state === "live");
  const done = tournaments.filter((entry) => entry.state === "complete");

  return (
    <>
      {live.length > 0 && (
        <div className="card">
          <p className="section-title">In progress</p>
          <ul className="list">
            {live.map((entry) => (
              <li key={entry.tournamentId} className="row">
                <span className="grow">
                  <span className="name">{entry.name ?? "Grayson Earl Jam"}</span>
                  <div className="muted">
                    {entry.size} racers · {entry.width} at a time
                  </div>
                </span>
                <span className="pill live">Live</span>
                <button className="btn small primary" onClick={() => setOpenId(entry.tournamentId)}>
                  Open
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      <NewTournament teamId={teamId} revision={revision} onError={onError} onCreated={setOpenId} />

      {done.length > 0 && (
        <div className="card">
          <p className="section-title">Finished</p>
          <ul className="list">
            {done.slice(0, 20).map((entry) => (
              <li key={entry.tournamentId} className="row">
                <span className="grow">
                  <span className="name">{entry.name ?? "Grayson Earl Jam"}</span>
                  <div className="muted">
                    {entry.createdAt ? new Date(entry.createdAt).toLocaleDateString() : ""}
                  </div>
                </span>
                <button className="btn small" onClick={() => setOpenId(entry.tournamentId)}>
                  View
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {tournaments.length === 0 && !list.loading && (
        <p className="muted">No tournaments yet. Pick a field above to start one.</p>
      )}
    </>
  );
}

/**
 * Creating a tournament. Task 8.6.
 *
 * **The draw happens here, not on the server.** The API takes an already-seeded roster in slot order,
 * because the draw is a ceremony that runs on a device (requirement 3.1) and re-running it server-side
 * would either duplicate the seeding logic or overrule what the family watched happen. The server still
 * validates exact fill, ownership and retirement, so this cannot invent a field.
 */
function NewTournament({
  teamId,
  revision,
  onError,
  onCreated,
}: {
  teamId: string;
  revision: number;
  onError: (message: string) => void;
  onCreated: (tournamentId: string) => void;
}) {
  const garage = useLoad(() => api.vehicles(teamId), [teamId, revision], onError);
  const { busy, run } = useAction(onError);
  const [width, setWidth] = useState<2 | 4>(2);
  const [size, setSize] = useState<8 | 16 | 32>(8);
  const [name, setName] = useState("");
  const [picked, setPicked] = useState<string[]>([]);
  const [vehicleClass, setVehicleClass] = useState<string | null>(null);

  const allActive: Vehicle[] = (garage.data?.vehicles ?? []).filter((v) => !v.isRetired);
  const classes = classesOf(allActive);

  /**
   * Requirement 7.13 — **a field cannot mix classes, and this screen did not know that.**
   *
   * The television has scoped its roster picker by class since classes existed. This screen never did, so
   * a phone could assemble a field spanning every class in the garage, and the backend would refuse it on
   * create — which is exactly what happened: an eight-car jam mixing 1:24, 1:64, Hot Wheel and Race Car
   * Monster Truck was refused, and the family raced it unrecorded without realising.
   *
   * Defaults to the first class rather than "all", because there is no legal "all": every tournament is
   * within one class, so offering a mixed pool would only be offering a mistake.
   */
  const chosenClass = vehicleClass ?? classes[0] ?? UNCLASSIFIED;
  const active = classes.length > 0 ? inClass(allActive, chosenClass) : allActive;

  // Requirement 2.7 — the roster must exactly fill the bracket, and 2.8 keeps a size that cannot be
  // filled visible with the shortfall rather than hiding it.
  const shortfall = Math.max(0, size - active.length);
  const complete = picked.length === size;

  const chooseClass = (name: string) => {
    setVehicleClass(name);
    // Clears the field, exactly as the television's class step does. Keeping picks across a class change is
    // how a mixed roster gets assembled without anybody choosing to.
    setPicked([]);
  };

  const toggle = (vehicleId: string) => {
    setPicked((current) =>
      current.includes(vehicleId)
        ? current.filter((entry) => entry !== vehicleId)
        : current.length < size
          ? [...current, vehicleId]
          : current,
    );
  };

  /**
   * The same convenience the television has: fill the field, keeping whatever was chosen by hand.
   *
   * Draws from the class-scoped pool, which is the whole point — filling a 1:24 bracket with a Hot Wheels
   * car is the mistake classes exist to prevent, and one press is the likeliest way to make it.
   */
  const surpriseMe = () => {
    const pool = active.map((vehicle) => vehicle.vehicleId).filter((id) => !picked.includes(id));
    for (let index = pool.length - 1; index > 0; index -= 1) {
      const swap = Math.floor(Math.random() * (index + 1));
      [pool[index], pool[swap]] = [pool[swap] as string, pool[index] as string];
    }
    setPicked((current) => [...current, ...pool.slice(0, size - current.length)]);
  };

  const create = () => {
    // Shuffled into slot order here: this is the draw.
    const seeded = [...picked];
    for (let index = seeded.length - 1; index > 0; index -= 1) {
      const swap = Math.floor(Math.random() * (index + 1));
      [seeded[index], seeded[swap]] = [seeded[swap] as string, seeded[index] as string];
    }
    void run(
      async () => {
        const created = await api.createTournament(teamId, {
          name: name.trim() || "Grayson Earl Jam",
          size,
          width,
          seededRoster: seeded,
        });
        setPicked([]);
        setName("");
        onCreated(created.tournamentId);
      },
    );
  };

  return (
    <div className="card">
      <p className="section-title">Start a tournament</p>

      <div className="field">
        <label>How many race at once?</label>
        {WIDTHS.map((option) => (
          <button
            key={option.value}
            className={`btn wide${width === option.value ? " primary" : ""}`}
            style={{ marginBottom: 6, justifyContent: "flex-start", textAlign: "left" }}
            onClick={() => setWidth(option.value)}
          >
            {option.label}
          </button>
        ))}
      </div>

      <div className="field" style={{ marginTop: 12 }}>
        <label>Field size</label>
        <div className="inline-form">
          {SIZES.map((option) => {
            const short = Math.max(0, option - active.length);
            return (
              <button
                key={option}
                className={`btn${size === option ? " primary" : ""}`}
                disabled={short > 0}
                style={{ flex: 1 }}
                onClick={() => {
                  setSize(option);
                  setPicked((current) => current.slice(0, option));
                }}
                title={short > 0 ? `${short} more needed` : undefined}
              >
                {option}
              </button>
            );
          })}
        </div>
        {shortfall > 0 && (
          <p className="muted">
            {shortfall} more {shortfall === 1 ? "truck" : "trucks"} needed for a {size}-bracket.
          </p>
        )}
      </div>

      {/*
        Requirement 7.13's class, chosen before the field. Only shown when there is a choice to make — the
        television skips this step for a single-class garage (11.2) and so does this.
      */}
      {classes.length > 1 && (
        <div className="field" style={{ marginTop: 12 }}>
          <label>Class</label>
          <div className="chips">
            {classes.map((name) => (
              <button
                key={name}
                className={`chip${sameClass(name, chosenClass) ? " on" : ""}`}
                onClick={() => chooseClass(name)}
              >
                {name} <span className="chip-count">{inClass(allActive, name).length}</span>
              </button>
            ))}
          </div>
          <p className="muted" style={{ marginTop: 6 }}>
            Every racer in a jam has to be the same class.
          </p>
        </div>
      )}

      <div className="field" style={{ marginTop: 12 }}>
        <label>
          Pick the field — {picked.length} of {size}
        </label>
        <button className="btn wide" disabled={complete} onClick={surpriseMe}>
          {picked.length === 0 ? `Surprise me — choose all ${size}` : `Fill the other ${size - picked.length}`}
        </button>
        <ul className="list" style={{ marginTop: 8 }}>
          {active.map((vehicle) => {
            const at = picked.indexOf(vehicle.vehicleId);
            return (
              <li key={vehicle.vehicleId}>
                <button
                  className={`lane${at >= 0 ? " picked" : ""}`}
                  onClick={() => toggle(vehicle.vehicleId)}
                >
                  <span className="place">{at >= 0 ? at + 1 : ""}</span>
                  <span className="grow name">{vehicle.name ?? "—"}</span>
                </button>
              </li>
            );
          })}
        </ul>
      </div>

      <div className="field" style={{ marginTop: 12 }}>
        <label>Name it (optional)</label>
        <input
          type="text"
          value={name}
          placeholder="Saturday Jam"
          maxLength={60}
          onChange={(event) => setName(event.target.value)}
        />
      </div>

      <button
        className="btn primary wide"
        style={{ marginTop: 12 }}
        disabled={busy || !complete}
        onClick={create}
      >
        {busy ? "Drawing…" : complete ? "Draw and start" : `Pick ${size - picked.length} more`}
      </button>
    </div>
  );
}
